import React, { useContext } from 'react';
import { styled } from '@mui/material/styles';

// Util
import { Handle, Position } from 'reactflow';
import { getIconFromPath } from '../../../../WorkflowProgressView/SessionComponentIcons';
import {
  calculateTimeElapsedForLastComponent,
  calculateTimeElapsedForWaiting,
  getBorderAndBGColorFromStatus,
} from '../../../../WorkflowProgressView/Util';

// Canvas
import { CanvasContext } from '../../../WorkflowCanvas';

// MUI
import { withStyles } from '@mui/styles';
import { Autocomplete, Button, TextField } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { ComponentNodeBodyView } from './ComponentNodeUtil/ComponentNodeBodyView';
import { EDGE_TYPES } from '../../../../../../utils/CanvasConstants';
import { COMPONENT_TYPE, STATUS } from './CanvasComponentUtil';
import { isLegacyForm } from '../../../../../../utils/formTypeConstants';
import { LoopHandles } from './ComponentNodeUtil/LoopHandles';

const TimeElapsedBox = styled('div')(({ color, isFormComp }) => ({
  backgroundColor: color.bg,
  border: `1px solid ${color.border}`,
  borderRadius: '4px',
  fontSize: '12px',
  width: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  position: 'absolute',
  top: isFormComp ? '-22px' : '-28px',
}));

const StyledButton = styled(Button)({
  color: 'white',
  '&.MuiButton-root': {
    border: props => `1px solid ${props.customcolor || '#1976d2'}`,
    backgroundColor: props => props.customcolor || '#1976d2',
  },
});

const StyledSpan = styled('span')({
  paddingLeft: '5px',
});

const styles = () => ({
  nodeInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'absolute',
    top: '74px',
  },
  nodeWrapper: {
    minWidth: '160px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  node: {
    display: 'flex',
    flexDirection: 'row',
  },
  rmvMargin: {
    margin: '0px!important',
    fontSize: '12px',
  },
  port: {
    position: 'relative !important',
    transform: 'unset !important',
    right: 'unset',
    top: '16px !important',
    height: '8px',
    width: '8px',
    backgroundColor: '#808080',
    ' &:hover': {
      backgroundColor: '#000000',
    },
    ' &:active': {
      backgroundColor: '#000000',
    },
  },
  filler: {
    width: '15px',
  },
  nodeInfoBackground: {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: '8px',
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
  threadContainer: {
    backgroundColor: 'rgba(142, 0, 209, 0.15)',
    border: '1px solid rgba(142, 0, 209, 1)',
    borderRadius: '4px',
    fontSize: '12px',
    width: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px',
    marginBottom: '4px',
    margin: '0 auto',
    maxHeight: '200px',
  },
  accountTreeIcon: {
    height: '14px',
    width: 'auto',
    marginRight: '2px',
  },
  selectedIcon: {
    filter: 'drop-shadow(6px 6px 0px rgba(0, 0, 0, 0.25))',
  },
  labelText: {
    maxWidth: '250px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  threadBoxContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  threadSelect: {
    '& .MuiInputBase-root': {
      minHeight: '24px',
      padding: '2px 4px',
    },
    '& .MuiAutocomplete-input': {
      padding: '0px 4px !important',
      fontSize: '12px',
    },
    '& .MuiAutocomplete-popper': {
      transform: 'scale(1) !important',
      transformOrigin: 'top left',
      position: 'absolute',
      zIndex: 1000,
      top: '100% !important',
      marginTop: '4px',
    },
    '& .MuiAutocomplete-listbox': {
      maxHeight: '200px',
    },
    '& .MuiPaper-root': {
      width: 'auto',
    },
    width: '200px',
    marginBottom: '4px',
    position: 'relative',
  },
});

const ProgressCanvasComponent = ({ id, data, isConnectable, classes, selected }) => {
  const { modeInfo, currentSelected, workflow, handleAlterThreadPath, isMainFlowSelected } = useContext(CanvasContext);
  const hasInput = data.hasInput;
  const hasOutput = data.hasOutput;

  const isSelected =
    data.sessionData && currentSelected.componentId === data.sessionData.componentId;

  const isLoopComponentAndSuccessful = component => {
    return (
      component.type === COMPONENT_TYPE.LOOP_THROUGH_LIST && component.status === STATUS.SUCCESS
    );
  };

  const getThreadText = info => {
    let data = info?.sessionData;
    if (!data) return null;

    let sessionData = info.allSessionData;
    let parentThreadId = data.thread.parentThreadId;

    const successStatusThreads = sessionData.components.filter(
      component =>
        isLoopComponentAndSuccessful(component) &&
        component.thread.parentThreadId === parentThreadId
    );

    let currentThreadIndex = successStatusThreads.findIndex(
      item => item.thread.threadId === data.thread.threadId
    );

    // Check if there's a converge_threads component downstream (means threads merge back)
    const hasConvergeThreads = sessionData.components.some(
      component => component.type === 'converge_threads'
    );

    // Build options for each child thread
    const threadOptions = successStatusThreads.map((item, index) => {
      const [key, value] = Object.entries(item.data).find(([k]) => k !== '.inputData') || [];
      return {
        key: key,
        label: `Thread ${index + 1}: ${value}`,
        threadId: item.thread.threadId,
        index: index + 1, // 1-indexed for display
        isMainFlow: false,
      };
    });

    // Add "Main Flow" option if converge_threads exists
    const options = hasConvergeThreads
      ? [
          {
            key: 'main_flow',
            label: 'Main Flow',
            threadId: 'MAIN_FLOW',
            index: 0,
            isMainFlow: true,
          },
          ...threadOptions,
        ]
      : threadOptions;

    // Determine current selection - use isMainFlowSelected from context
    const mainFlowOption = options.find(opt => opt.isMainFlow);
    const currentValue = isMainFlowSelected && mainFlowOption
      ? mainFlowOption
      : (options.find(opt => opt.threadId === data.thread.threadId) || options[hasConvergeThreads ? 1 : 0]);
    const displayText = currentValue?.isMainFlow 
      ? 'Main Flow' 
      : `Thread ${currentThreadIndex + 1} of ${successStatusThreads.length}`;

    return (
      <div>
        <div className={classes.threadContainer}>
          <AccountTreeIcon className={classes.accountTreeIcon} />
          <span>{displayText}</span>
        </div>
        <div className={classes.threadBoxContainer}>
          <Autocomplete
            className={classes.threadSelect}
            options={options}
            value={currentValue}
            disablePortal
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.threadId === value?.threadId}
            onOpen={event => event.stopPropagation()}
            onChange={(event, newValue) => {
              if (newValue) {
                handleAlterThreadPath(data.thread.threadId, newValue.threadId);
              }
            }}
            renderInput={params => <TextField {...params} variant="outlined" size="small" />}
          />
        </div>
      </div>
    );
  };

  // Returns true/false depending on if a component is the last of its branch
  const isLastComponentOnBranch = data => {
    try {
      for (let i = 0; i < workflow.length; i++) {
        let comp = workflow[i];
        if (comp.type !== 'link') continue;

        // Need to check if the current comp is the source of any links
        if (comp.source === data.sessionData.componentId) return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  // Attempts to find the link connected to a component
  // If it exists
  const findPreviousComponentLink = data => {
    try {
      for (let i = 0; i < workflow.length; i++) {
        let comp = workflow[i];
        if (comp.type !== EDGE_TYPES.LINK) continue;
        if (comp.target === data.sessionData.componentId) {
          return comp;
        }
      }
    } catch (e) {
      return;
    }
  };

  // Returns true/false depending on if it's a legacy form type component or not
  const isFormComp = data => {
    if (!data) return false;
    return isLegacyForm(data.type);
  };

  const calculateTime = data => {
    if (!data || !data.sessionData) return;

    // Handle waiting
    if (data.sessionData.status === STATUS.WAITING) {
      let time = calculateTimeElapsedForWaiting(data.sessionData);
      if (!time) return;
      return (
        <TimeElapsedBox
          color={getBorderAndBGColorFromStatus(data.sessionData.status)}
          isFormComp={isFormComp(data.sessionData)}
        >
          <span id={`waiting-time-${data.sessionData.componentId}`}>{time}</span>
        </TimeElapsedBox>
      );
    } else {
      // Handle checking last component

      // If it has an output and it isn't the last comp on the branch, return
      if (data.hasOutput && !isLastComponentOnBranch(data)) return;

      // Need to find previous component
      let prevLink = findPreviousComponentLink(data);
      if (!prevLink) return;

      let endComponentTime = calculateTimeElapsedForLastComponent(
        prevLink,
        data.allSessionData.components
      );
      if (!endComponentTime) return;

      return (
        <TimeElapsedBox
          color={getBorderAndBGColorFromStatus(data.sessionData.status)}
          isFormComp={isFormComp(data.sessionData)}
        >
          <span id={`end-time-${data.sessionData.componentId}`}>{endComponentTime}</span>
        </TimeElapsedBox>
      );
    }
  };

  if (modeInfo == undefined) {
    return (
      <>
        <div className={classes.nodeWrapper}>
          <div className={classes.node}>
            {hasInput == true ? (
              <Handle
                className={`${classes.port}`}
                type="target"
                position={Position.Left}
                isConnectable={true}
              />
            ) : (
              <div className={classes.filler} />
            )}
            {data.fromWorkflowPath ? (
              <StyledButton
                size="small"
                customcolor={data.colour}
                variant="outlined"
                className="draftVersionButton"
              >
                <StyledSpan>{data.label}</StyledSpan>
              </StyledButton>
            ) : (
              getIconFromPath(data.logo, data.colour, data.colour)
            )}

            {hasOutput == true ? (
              <Handle
                className={classes.port}
                position={Position.Right}
                type="source"
                isConnectable={isConnectable}
              />
            ) : (
              <div className={classes.filler} />
            )}
          </div>
          {data.fromWorkflowPath ? (
            ''
          ) : (
            <div className={classes.nodeInfoBackground}>
              <>
                <p className={`${classes.rmvMargin} bold`}>{data.displayName}</p>
                <p className={`${classes.rmvMargin} ${classes.labelText}`}>{data.label}</p>
              </>
            </div>
          )}
        </div>
      </>
    );
  } else {
    return (
      <div
        onClick={() => {
          data.setModalData({ ...data.sessionData, hasRun: true });
          if (!data.sessionData) data.setModalData({ componentId: '', hasRun: false });
        }}
      >
        <div className={classes.nodeWrapper}>
          {data.type === COMPONENT_TYPE.LOOP_THROUGH_LIST && getThreadText(data)}
          {data && calculateTime(data)}
          <div className={classes.node}>
            {hasInput == true ? (
              <Handle
                className={classes.port}
                type="target"
                position={Position.Left}
                isConnectable={true}
              />
            ) : (
              <div className={classes.filler} />
            )}
            <div className={isSelected && classes.selectedIcon}>
              {data.fromWorkflowPath ? (
                <StyledButton
                  size="small"
                  customcolor={data.colour}
                  variant="outlined"
                  className="draftVersionButton"
                >
                  <StyledSpan>{data.label}</StyledSpan>
                </StyledButton>
              ) : (
                <ComponentNodeBodyView baseColor={data.baseColor} data={data} classes={classes} />
              )}
            </div>
            {hasOutput == true ? (
              <Handle
                className={classes.port}
                position={Position.Right}
                type="source"
                isConnectable={isConnectable}
              />
            ) : (
              <div className={classes.filler} />
            )}
            <LoopHandles />
          </div>
        </div>
      </div>
    );
  }
};
export default withStyles(styles)(ProgressCanvasComponent);
